<template>
    <div class="reservation-calendar">

		<form ref="form" v-on:submit.prevent="onSubmit" novalidate>
	
            <!-- company_id -->
            <div v-if="!company" class="form-group row">
                <label for="company_id" class="col-sm-3 col-form-label">{{ 'reserve.company_id' | translate }}</label>
                <div class="col-sm-9">
                    <select class="form-control" style="width: auto;" :class="{'is-invalid': errors.company_id}" id="company_id" v-model="form.company_id" @change="onCompanyChange" required autocomplete="false">
                        <option value=""></option>
                        <option v-for="company in companies" :key="company.id" :value="company.id">{{ company.name }}</option>
                    </select>

                    <div v-if="errors.company_id" class="invalid-feedback">
                        {{ errors.company_id[0] }}
                    </div> 
                </div>
            </div>

			<div v-if="company">
                <div v-if="showLoginStatus">
                    <div v-if="current_user" class="reservation-user-info float-right">
                        {{ 'reserve.welcome' | translate }} {{ current_user.first_name }} {{ current_user.last_name }}
                        <a @click="logout()" class="text-primary">{{ 'reserve.logout' | translate }}</a>
                        <a :href="routes.user.dashboard" target="_blank" class="text-primary">{{ 'reserve.dashboard' | translate }}</a>
                    </div>
                    <div v-else class="reservation-user-login float-right">
                        <a @click="showLogin()" class="text-primary">{{ 'reserve.login' | translate }}</a>
                    </div>
				</div>

				<h1 class="d-inline-block mr-4">{{ company.name }}</h1>
                
                <img v-if="company.picture" class="company-logo-medium" :src="company.picture" :alt="company.name" :title="company.name">
			</div>

			<div v-if="selected_company && selected_company.reservation_description" class="row">
				<div class="col-md-12">
					<p :inner-html.prop="selected_company.reservation_description | nl2br"></p>
				</div>
			</div>

            <!-- num_guests -->
            <div class="form-group row">
                <label for="num_guests" class="col-sm-3 col-form-label">{{ 'reserve.num_guests' | translate }}</label>
                <div class="col-sm-9">
                        <select class="form-control" style="width: auto;" :class="{'is-invalid': errors.num_guests}" id="num_guests" v-model="form.num_guests" @change="onNumGuestsChange" required autocomplete="false">
                            <option value="0">0</option>
                            <option v-for="n in maxGroupSize" :key="n" :value="n">{{ n }}</option>
                        </select>

                    <div v-if="errors.num_guests" class="invalid-feedback">
                        {{ errors.num_guests[0] }}
                    </div> 
                </div>
            </div>

            <div v-if="true || form.company_id > 0 && form.num_guests > 0">

                <div class="row">
                    <div class="col-md-3">
                        <v-date-picker
                            ref="calendar"
                            mode="single"
                            v-model="form.date"
							:locale="locale"
                            :min-page="{ year: (new Date()).getFullYear(), month: (new Date()).getMonth() + 1 }"
                            :attributes="attrs"
                            :available-dates="availableDates"
                            is-inline
                            @update:from-page="loadDates"
                            @dayclick="loadTimes"
                        />
                    </div>
                    <div class="col-md-1">
                    </div>
                    <div class="col-md-8" v-if="form.date">

                        <div class="mb-4">
                            <div v-if="selected_company.max_guests_inside > 0" class="btn mr-3" :class="[form.inside ? 'btn-primary' : 'btn-gray']" @click="toggleInside(true)">
                                {{ guests_inside_text }}
                            </div>
                            <div v-if="selected_company.max_guests_outside > 0" class="btn" :class="[!form.inside ? 'btn-primary' : 'btn-gray']" @click="toggleInside(false)">
                                {{ guests_outside_text }}
                            </div>
                        </div>

					    <p class="h4 text-primary font-weight-bold">{{ 'reserve.reserve_select_time' | translate }}</p>

                        <p class="h5 mb-3">{{ form.date | moment("dddd D MMMM YYYY") }}</p>

                        <div class="times" v-for="(items, descriptionGroup) in timeslots" :key="descriptionGroup">
                            
					        <p v-if="has_multiple_descriptions" class="h5 text-primary font-weight-bold">
                                <span v-if="descriptionGroup != 'null'">{{ descriptionGroup }}</span>
                                <span v-else>{{ 'reserve.default_description' | translate }}</span>
                            </p>

                            <div class="row">
                                <div class="col-md-4 mb-3" v-for="timeslot in items" :key="timeslot.id">
                                    <button 
                                        type="button"
                                        class="btn text-left" 
                                        :class="getTimeslotClass(timeslot)" 
                                        :disabled="!timeslot.available"
                                        @click="onTimeslotClick(timeslot)"
                                    >
                                        {{ timeslot.label }}
                                        <small v-if="timeslot.description"><br>{{ timeslot.description }}</small>
                                    </button>
                                    <div v-if="!timeslot.available && timeslot.reason" class="" :class="[timeslot.reserved ? 'text-success' : 'text-danger ml-3']">{{ timeslot.reason }}</div>
                                </div>
                            </div>

                        </div>

                        <div class="legend mt-4">
                            <div><i class="fa fa-circle text-primary"></i> {{ 'reserve.legend_available' | translate }}</div>
                            <div><i class="fa fa-circle text-gray"></i> {{ 'reserve.legend_unavailable' | translate }}</div>
                        </div>

                    </div>
                </div>

            
                <div v-if="form.timeslot_id > 0" class="mt-4">
                    <p>{{ 'reserve.info' | translate }}</p>
                    <div class="row">
                        <div class="col-md-6">

                            <!-- firstname -->
                            <div v-if="!current_user || !current_user.first_name" class="form-group row">
                                <label for="firstname" class="col-sm-3 col-form-label">{{ 'reserve.firstname' | translate }}<span class="required">*</span></label>
                                <div class="col-sm-9">
                                    <input type="text" :class="{ 'form-control': true, 'is-invalid': errors.firstname}" id="firstname" v-model="form.firstname" required>
                                    <div v-if="errors.firstname" class="invalid-feedback">
                                        {{ errors.firstname[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- lastname -->
                            <div v-if="!current_user || !current_user.last_name" class="form-group row">
                                <label for="lastname" class="col-sm-3 col-form-label">{{ 'reserve.lastname' | translate }}<span class="required">*</span></label>
                                <div class="col-sm-9">
                                    <input type="text" :class="{ 'form-control': true, 'is-invalid': errors.lastname}" id="lastname" v-model="form.lastname" required>
                                    <div v-if="errors.lastname" class="invalid-feedback">
                                        {{ errors.lastname[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- telephone -->
                            <div v-if="!current_user || !current_user.telephone" class="form-group row">
                                <label for="telephone" class="col-sm-3 col-form-label">{{ 'reserve.telephone' | translate }}<span class="required">*</span></label>
                                <div class="col-sm-9">
                                    <input type="telephone" :class="{ 'form-control': true, 'is-invalid': errors.telephone}" id="telephone" v-model="form.telephone" required>
                                    <div v-if="errors.telephone" class="invalid-feedback">
                                        {{ errors.telephone[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- email -->
                            <div v-if="!current_user" class="form-group row">
                                <label for="email" class="col-sm-3 col-form-label">{{ 'reserve.email' | translate }}<span class="required">*</span></label>
                                <div class="col-sm-9">
                                    <input type="email" :class="{ 'form-control': true, 'is-invalid': errors.email}" id="email" v-model="form.email" required>
                                    <div v-if="errors.email" class="invalid-feedback">
                                        {{ errors.email[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- password -->
                            <div v-if="!current_user" class="form-group row">
                                <label for="password" class="col-sm-3 col-form-label">{{ 'reserve.password' | translate }}<span class="required">*</span></label>
                                <div class="col-sm-9">
                                    <input type="password" :class="{ 'form-control': true, 'is-invalid': errors.password}" id="password" v-model="form.password" required>
                                    <div v-if="errors.password" class="invalid-feedback">
                                        {{ errors.password[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- password_confirmation -->
                            <div v-if="!current_user" class="form-group row">
                                <label for="password_confirmation" class="col-sm-3 col-form-label">{{ 'reserve.password_confirmation' | translate }}<span class="required">*</span></label>
                                <div class="col-sm-9">
                                    <input type="password" :class="{ 'form-control': true, 'is-invalid': errors.password_confirmation}" id="password_confirmation" v-model="form.password_confirmation" required>
                                    <div v-if="errors.password_confirmation" class="invalid-feedback">
                                        {{ errors.password_confirmation[0] }}
                                    </div> 
                                </div>
                            </div>

                        </div>
                        <div v-if="!current_user" class="col-md-6">

                            <!-- address -->
                            <div class="form-group row">
                                <label for="address" class="col-sm-3 col-form-label">{{ 'reserve.address' | translate }}</label>
                                <div class="col-sm-9">
                                    <input type="text" :class="{ 'form-control': true, 'is-invalid': errors.address}" id="address" v-model="form.address">
                                    <div v-if="errors.address" class="invalid-feedback">
                                        {{ errors.address[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- zipcode -->
                            <div class="form-group row">
                                <label for="zipcode" class="col-sm-3 col-form-label">{{ 'reserve.zipcode' | translate }}</label>
                                <div class="col-sm-9">
                                    <input type="text" :class="{ 'form-control': true, 'is-invalid': errors.zipcode}" id="zipcode" v-model="form.zipcode">
                                    <div v-if="errors.zipcode" class="invalid-feedback">
                                        {{ errors.zipcode[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- city -->
                            <div class="form-group row">
                                <label for="city" class="col-sm-3 col-form-label">{{ 'reserve.city' | translate }}</label>
                                <div class="col-sm-9">
                                    <input type="text" :class="{ 'form-control': true, 'is-invalid': errors.city}" id="city" v-model="form.city">
                                    <div v-if="errors.city" class="invalid-feedback">
                                        {{ errors.city[0] }}
                                    </div> 
                                </div>
                            </div>
                            
                            <!-- country_id -->
                            <div class="form-group row">
                                <label for="country_id" class="col-sm-3 col-form-label">{{ 'reserve.country_id' | translate }}</label>
                                <div class="col-sm-9">
                                    <select class="form-control" :class="{'is-invalid': errors.country_id}" id="country_id" v-model="form.country_id">
                                        <option value="">Geen</option>
                                        <option v-for="country in settings.countries" :key="country.id" :value="country.id">{{ country['name_' + language] }}</option>
                                    </select>

                                    <div v-if="errors.country_id" class="invalid-feedback">
                                        {{ errors.country_id[0] }}
                                    </div> 
                                </div>
                            </div>

                        </div>
                    </div>

                    <div class="row mb-2">
                        <div class="col-md-12">
                                    
                            <div class="form-group">
                                <label for="comments" class="form-label">{{ 'reserve.comments' | translate }}</label>
                                <textarea type="text" :class="{ 'form-control': true, 'is-invalid': errors.comments}" id="comments" v-model="form.comments"></textarea>
                                <div v-if="errors.comments" class="invalid-feedback">
                                    {{ errors.comments[0] }}
                                </div> 
                            </div>

                        </div>
                    </div>

					<!-- corona_1 
					<div class="form-group">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="corona_1" :class="{'is-invalid': errors.corona_1}" v-model="form.corona_1">
                            <label class="custom-control-label" for="corona_1">{{ 'reserve.corona_1' | translate }}</label>
							<div v-if="errors.corona_1" class="invalid-feedback">
								{{ errors.corona_1[0] }}
							</div>
                        </div>
					</div> -->

					<!-- corona_2 
					<div class="form-group">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="corona_2" :class="{'is-invalid': errors.corona_2}" v-model="form.corona_2">
                            <label class="custom-control-label" for="corona_2">{{ 'reserve.corona_2' | translate }}</label>
							<div v-if="errors.corona_2" class="invalid-feedback">
								{{ errors.corona_2[0] }}
							</div>
                        </div>
					</div> -->

					<!-- corona_3 -->
					<div class="form-group">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="corona_3" :class="{'is-invalid': errors.corona_3}" v-model="form.corona_3">
                            <label class="custom-control-label" for="corona_3">{{ 'reserve.corona_3' | translate }}</label>
							<div v-if="errors.corona_3" class="invalid-feedback">
								{{ errors.corona_3[0] }}
							</div>
                        </div>
					</div>

					<!-- receive_newsletter -->
					<div v-if="!current_user" class="form-group">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="receive_newsletter" :class="{'is-invalid': errors.receive_newsletter}" v-model="form.receive_newsletter">
                            <label class="custom-control-label" for="receive_newsletter">{{ 'reserve.receive_newsletter' | translate }}</label>
							<div v-if="errors.receive_newsletter" class="invalid-feedback">
								{{ errors.receive_newsletter[0] }}
							</div>
                        </div>
					</div>

                    <div v-if="selected_company && selected_company.reservation_deposit > 0" class="mt-4">
                        <b>{{ 'reserve.deposit' | translate }} {{ form.num_guests }} x {{ selected_company.reservation_deposit | currency }} = {{ selected_company.reservation_deposit * form.num_guests | currency }}</b>
                    </div>

			        <button type="submit" class="btn btn-primary mt-4">{{ 'reserve.submit' | translate }}</button>

                </div>

            </div>

        </form>
        
		<v-style v-if="font_family_url">
            @import url('{{ font_family_url }}');
		</v-style>

		<v-style>
			:root {
				--timeslot-primary-color: {{ color_primary }};
				--timeslot-primary-color-faded: {{ color_primary_faded }};
				--timeslot-primary-color-text: {{ color_primary_text }};
                --timeslot-font-family: {{ font_family }}
			}
		</v-style>


    </div>
</template>

<script>
    export default {
		name: 'reservation-calendar',
		props: [
			// 'user',
			'company',
            'companies',
            'showLoginStatus',
			// 'countries',
			// 'reserveUrl',
			// 'loginUrl',
			// 'logoutUrl',
			// 'dashboardUrl',
			// 'getDatesUrl',
			// 'getTimesUrl'
		],
		data() {
			return {
				csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
				locale: window.default_locale,
				// current_user: null,
				selected_company: null,
                // color_primary: 'var(--primary)', //'#6610f2',
				form: {
                    company_id: 0,
                    num_guests: 0,
                    date: null,
                    inside: true,
					timeslot_id: 0,
                    
					firstname: '',
					lastname: '',
					telephone: '',
					email: '',
					password: '',
                    password_confirmation: '',
                    address: '',
					zipcode: '',
					city: '',
					comments: '',
					country_id: 'NL',
                    corona_1: false,
                    corona_2: false,
                    corona_3: false,
                    receive_newsletter: true,
                },
                errors: {},
                attrs: [],
                availableDates: [''],
                timeslots: [],
                lastPage: null,
                lastDay: null
            }
		},
		mounted: function() {
            /* console.log(this.current_user);
            this.user = this.current_user;

			if(this.user) {
				this.current_user = this.user;
			} else {
				this.current_user = null;
			} */

			if(this.company) {
				this.selected_company = this.company;
				this.form.company_id = this.company.id;
			} else {
				this.selected_company = null;
			}
		},
        methods: {
            onCompanyChange() {
                if(this.form.company_id) {
                    let company = _.find(this.companies, ['id', this.form.company_id]);
                    if(company) { //  && company.color_primary
						this.selected_company = company;
                        // this.color_primary = company.color_primary;
                        if(this.form.inside && company.max_guests_inside <= 0) {
                            this.form.inside = false;
                        }
                        if(!this.form.inside && company.max_guests_outside <= 0) {
                            this.form.inside = true;
                        }
                    } else {
						this.selected_company = null;
                    }
                    
                    if(this.form.num_guests > this.maxGroupSize) {
                        this.form.num_guests = this.maxGroupSize;
                    }
                }
                this.loadDates();
            },

            onNumGuestsChange() {
                if(this.selected_company) {
                    if(this.form.inside && this.selected_company.max_guests_inside <= 0) {
                        this.form.inside = false;
                    }
                    if(!this.form.inside && this.selected_company.max_guests_outside <= 0) {
                        this.form.inside = true;
                    }
                }
                
                this.loadDates();
                this.loadTimes();
            },

            loadDates(page) {
                if(!page) {
                    page = this.lastPage;
                }
                if(!page) return;
                this.lastPage = page;

				this.attrs = [];
                this.availableDates = [''];
                this.form.date = null;
                this.form.timeslot_id = 0;
                this.timeslots = [];

                if(this.form.company_id <= 0) return;
				if(this.form.num_guests <= 0) return;
				
				this.showLoader();

                axios.post(this.routes.reserve.get_dates, {
                    company_id: this.form.company_id,
                    month: page.month,
                    year: page.year,
                    num_guests: this.form.num_guests
                }) // qs.stringify(this.serverParams)
                .then(response => {
					this.hideLoader();
					
                    _.each(response.data.dates, (active, date) => { 
                        if(active) {
                            this.availableDates.push(date);
                            this.attrs.push({
                                key: date,
                                highlight: 'red',
                                dates: date
                            });
                        }
                    });
                }).catch(error => {
					this.hideLoader();
					
                    console.error(error);
                    Swal.fire({
                        icon: 'error',
                        title: this.translate('error'),
                    });
                });
            },

            loadTimes(day) {
                if(!day) {
                    day = this.lastDay;
                }
                if(!day) return;
                this.lastDay = day;

                if(this.form.company_id <= 0) return;
                if(this.form.num_guests <= 0) return;
                
                this.form.timeslot_id = 0;
                this.timeslots = [];

				this.showLoader();

                axios.post(this.routes.reserve.get_times, {
                    company_id: this.form.company_id,
                    date: day.id,
                    num_guests: this.form.num_guests,
                    inside: this.form.inside
                }) // qs.stringify(this.serverParams)
                .then(response => {
					this.hideLoader();
					
                    // this.timeslots = response.data.times;
                    this.timeslots = _.groupBy(response.data.times, 'description');
                    
                    if(!this.form.date) {
                        this.form.date = day.date;
                    }
                }).catch(error => {
					this.hideLoader();

                    console.error(error);
                    Swal.fire({
                        icon: 'error',
                        title: this.translate('error'),
                    });
                });
            },

            toggleInside(inside) {
                if(inside == this.form.inside) return; // Nothing changes

                this.form.inside = inside;
                this.loadTimes();
            },

            onTimeslotClick(timeslot) {
                if(timeslot.available) { 
					this.form.timeslot_id = timeslot.id;
                }
            },

			onSubmit() {

                let form = {...this.form};
				
				form.date = Vue.moment(form.date).format('YYYY-MM-DD');

				this.errors = {};
				
				this.showLoader();

				axios.post(this.routes.reserve.reserve, form)
				.then(response => {
					this.hideLoader();

					if(response.data && response.data.user) {
						this.current_user = response.data.user;
					}

					if(response.data.payment_url) {
						window.location.href = response.data.payment_url;
                    }
                    
					this.loadTimes();

					Swal.fire({
						icon: 'success',
						title: this.translate('reserve.success'),
					}).then(() => {
                        if(response.data.payment_url) {
                            window.location.href = response.data.payment_url;
                            return;
                        }
						if(!this.current_user) {
							window.location.reload();
						}
					});
				}).catch(error => {
					this.hideLoader();

					if(error && error.response && error.response.status == 422) {
						this.errors = error.response.data.errors;

						if(this.errors.length <= 0 && error.response.data.message) {
							Swal.fire({
								icon: 'error',
								title: error.response.data.message,
							});
						}
					} else {
						Swal.fire({
							icon: 'error',
							title: this.translate('error'),
						});
					}
				});

				return true;
            },

			showLogin() {
                window.passwordResetClick = (function() {
                    this.showPasswordReset();
                }).bind(this);

				Swal.fire({
					title: this.translate('reserve.login-popup.title'),

					html: '' +
					'<div class="form-group">' +
					'	<label for="email" class="float-left">' + this.translate('reserve.email') + '</label>' +
					'	<input type="email" name="email" id="email" placeholder="' + this.translate('reserve.email') + '" maxlength="191" required="required" class="form-control">' +
					'</div>' +
					'<div class="form-group">' +
					'	<label for="password" class="float-left">' + this.translate('reserve.password') + '</label>' +
					'	<input type="password" name="password" id="password" placeholder="' + this.translate('reserve.password') + '" required="required" class="form-control">' +
                    '</div>' +
                    '<a class="w-100 text-left float-left" href="#" onclick="passwordResetClick()">' + this.translate('reserve.forgot_password') + '</a>', 
                    // '<a href="#" class="btn btn-sm btn-outline-info m-1"><i class="fab fa-google"></i> Google</a>',
					confirmButtonText: this.translate('reserve.login-popup.button'),
					preConfirm: () => {
						let email = Swal.getPopup().querySelector('#email').value;
						let password = Swal.getPopup().querySelector('#password').value;
						
						return axios.post(this.routes.auth.login, { 
							email: email, 
							password: password,
							remember: 1,
							role: 'user',
						}).then(response => {
                            if(response && response.data && response.data.user) {
                                this.current_user = response.data.user;
                            }
                            return true;
                        }).catch(error => {
                            if(error.response.data.errors.email && error.response.data.errors.email[0]) {
                                Swal.showValidationMessage(error.response.data.errors.email[0]);
                            } else {
                                Swal.showValidationMessage(this.translate('reserve.login-popup.error'));
                            }
                        });
						/* if (username === '' || password === '') {
							Swal.showValidationMessage(this.translate('reserve.login.empty_fields'));
						}
						return {username: username, password: password} */
					}
				}).then((result) => {
					// result.value.username
					// result.value.password
					// Swal.showValidationMessage(this.translate('reserve.login.empty_fields'));

					// Swal.fire(`Username: ${result.value.username}\nPassword: ${result.value.password}`)
				})
			},

            showPasswordReset() {
				Swal.fire({
					title: this.translate('reserve.password-popup.title'),

					html: '' +
					'<div class="form-group">' +
					'	<label for="email" class="float-left">' + this.translate('reserve.email') + '</label>' +
					'	<input type="email" name="email" id="email" placeholder="' + this.translate('reserve.email') + '" maxlength="191" required="required" class="form-control">' +
					'</div>',
                    // '<a href="#" class="btn btn-sm btn-outline-info m-1"><i class="fab fa-google"></i> Google</a>',
					confirmButtonText: this.translate('reserve.password-popup.button'),
					preConfirm: () => {
						let email = Swal.getPopup().querySelector('#email').value;
						
						return axios.post(this.routes.auth.password_email, { 
							email: email, 
						}).then(response => {
                            console.log(response);
                            if(response.data && response.data.message) {
                                Swal.fire({
                                    icon: 'success',
                                    title: response.data.message,
                                })
                            }
                            return true;
                        }).catch(error => {
                            if(error.response.data.message) {
                                Swal.showValidationMessage(error.response.data.message);
                            } else {
                                Swal.showValidationMessage(this.translate('reserve.password-popup.error'));
                            }
                        });
						/* if (username === '' || password === '') {
							Swal.showValidationMessage(this.translate('reserve.login.empty_fields'));
						}
						return {username: username, password: password} */
					}
				}).then((result) => {
					// result.value.username
					// result.value.password
					// Swal.showValidationMessage(this.translate('reserve.login.empty_fields'));

					// Swal.fire(`Username: ${result.value.username}\nPassword: ${result.value.password}`)
				})
            },

			logout() {		
				this.showLoader();

				axios.get(this.routes.auth.logout, {})
				.then(response => {
					this.hideLoader();
					this.current_user = null;
				}).catch(error => {
					this.hideLoader();

					Swal.fire({
						icon: 'error',
						title: this.translate('error'),
					});
				});
            },
			
			getTimeslotClass(timeslot) {
				let selected = this.form.timeslot_id == timeslot.id;

				if(timeslot.reserved) {
					return selected ? 'btn-danger' : 'btn-success';
				}

				return [					
					selected ? 'btn-primary' : 'btn-light', 
					!selected && timeslot.available ? 'text-primary' : ''
				];
			},
			
        },
        computed: {
            guests_inside_text: function() {
                if (this.selected_company.guests_inside_text) {
                    return this.selected_company.guests_inside_text;
                }

                return this.translate('reserve.book_inside');
            },
            guests_outside_text: function() {
                if (this.selected_company.guests_outside_text) {
                    return this.selected_company.guests_outside_text;
                }

                return this.translate('reserve.book_outside');
            },
            maxGroupSize: function() {
                return this.selected_company ? this.selected_company.max_group_size : 8;
            },
            has_multiple_descriptions: function() {
                return Object.keys(this.timeslots).length > 1;
            },
			color_primary: function() {
                return this.selected_company && this.selected_company.color_primary ? this.selected_company.color_primary : 'var(--primary)';
			},
            color_primary_faded: function() {
                return this.hexToRgbA(this.color_primary, 0.25);
			},
			color_primary_text: function() {
				let rgb = this.hexToRgbArray(this.color_primary);
				let yiq = ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000;
				let yiq_threshold = 150;

				return yiq >= yiq_threshold ? 'var(--dark)' : 'var(--white)';
            },
            font_family: function() {
                if(this.font_family_url && this.selected_company.font_family) {
                    return this.selected_company.font_family;
                }
                return 'var(--font-family-sans-serif)';
            },
            font_family_url: function() {
                if(this.selected_company && this.selected_company.font_family_url) {
                    return this.selected_company.font_family_url;
                }
                return '';
            }
        }
    }
</script>
